import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useLocation, useMatch, useSearchParams } from 'react-router-dom';

import { MODULE_FILTER_EXCLUSIONS } from 'MODULES/universal-filters/config';
import { universalFiltersConfig } from 'MODULES/universal-filters/useUniversalFilters/universalFiltersConfig';
import { useFetchFilters } from 'MODULES/universal-filters/useUniversalFilters/useFetchFilters';
import {
  ActionEnum,
  modifyUniversalParams,
} from 'MODULES/universal-filters/useUniversalFilters/utils/modifyUniversalParams';
import { parseParams } from 'MODULES/universal-filters/useUniversalFilters/utils/parseParams';
import {
  FilterExpression,
  UniversalFilter,
} from 'MODULES/universal-filters/utils/types';
import { Loading } from 'SRC/components/Loading/Loading';
import { pathTo } from 'SRC/components/RoutesMain/routing';
import { FilterConfig } from 'SRC/fetch/api/observability';
import { useParamOperations } from 'SRC/hooks/searchParams/useParamOperations';
import { noop } from 'UTILS/noop';

interface UniversalFiltersContext {
  lastSearchParams: URLSearchParams;
  setLastSearchParams: (params: URLSearchParams) => void;
  setFilterParams: (filters: Map<string, UniversalFilter>) => void;
  clearParams: (additionalParams?: string[]) => void;
  addParams: (param: Record<string, string[]>) => void;
  removeFilterParams: (filters: Map<string, UniversalFilter>) => void;
  isLoading: boolean;
  universalFilters: Map<string, FilterExpression>;
  quickFilterMap: Map<string, FilterConfig>;
  filtersConfig: Map<string, FilterConfig>;
}

export const UniversalFiltersContext = createContext<UniversalFiltersContext>({
  lastSearchParams: new URLSearchParams(),
  setLastSearchParams: noop,
  setFilterParams: noop,
  clearParams: noop,
  addParams: noop,
  removeFilterParams: noop,
  isLoading: false,
  universalFilters: new Map(),
  quickFilterMap: new Map(),
  filtersConfig: new Map(),
});

export const UniversalFiltersProvider = ({ children }) => {
  const { isLoading } = useFetchFilters();
  const location = useLocation();
  const [lastSearchParams, setLastSearchParams] = useState(
    new URLSearchParams(),
  );
  const [lastPathname, setLastPathname] = useState(location.pathname);
  const [searchParams, setSearchParams] = useSearchParams();

  const parsedKeys = universalFiltersConfig((state) => state.parsedKeys);
  const universalFilters = universalFiltersConfig(
    (state) => state.universalFilters,
  );
  const filtersConfig = universalFiltersConfig((state) => state.filtersConfig);
  const quickFilterMap = universalFiltersConfig((state) => state.quickFilters);
  const paramsInitialized = universalFiltersConfig(
    (state) => state.paramsInitialized,
  );

  const isPaymentsList = useMatch(pathTo.payments);
  const isObsDashboard = useMatch(pathTo.observability + '/:id/*');

  // Map route matches to module names for exclusion lookup
  const getCurrentModuleFilterExclusions = () => {
    if (isPaymentsList) {
      return MODULE_FILTER_EXCLUSIONS.payments;
    }
    if (isObsDashboard) {
      return MODULE_FILTER_EXCLUSIONS.observability;
    }
    return [];
  };

  // Filter out excluded keys for the current route
  const filteredUniversalFilters = useMemo(() => {
    const excludedKeys = getCurrentModuleFilterExclusions();
    if (excludedKeys.length === 0) {
      return universalFilters;
    }

    const filtered = new Map<string, FilterExpression>();
    universalFilters.forEach((value, key) => {
      if (!excludedKeys.includes(key)) {
        filtered.set(key, value);
      }
    });
    return filtered;
  }, [universalFilters, isPaymentsList, isObsDashboard]);

  const { addParams, removeParams } = useParamOperations(
    setSearchParams,
    'array',
  );

  useEffect(() => {
    /**
     * This tracks the search params when we are on the payments and observability dashboard pages, so that we can
     * restore the filters when we navigate between them.
     */
    if (isLoading || !paramsInitialized) {
      return;
    }

    if (isPaymentsList || isObsDashboard) {
      setLastSearchParams(new URLSearchParams(searchParams));
    }
  }, [
    searchParams,
    isPaymentsList,
    isObsDashboard,
    isLoading,
    paramsInitialized,
  ]);

  useEffect(() => {
    /**
     * This helps us restore filters when we navigate between payments and observability pages.
     */
    if (isLoading) {
      return;
    }

    setLastPathname(location.pathname);

    if (!lastSearchParams.toString()) {
      parseParams(searchParams, parsedKeys, filtersConfig);
      return;
    }

    if (!(isPaymentsList || isObsDashboard)) {
      parseParams(searchParams, parsedKeys, filtersConfig);
      return;
    }

    // Check if we've navigated to a different page
    const hasNavigated = lastPathname !== location.pathname;

    const missingKeys = Array.from(lastSearchParams.keys()).filter(
      (key) => !searchParams.has(key),
    );

    if (!missingKeys.length) {
      parseParams(searchParams, parsedKeys, filtersConfig);
      return;
    }

    // Only restore missing parameters if we've navigated between pages
    // If we're on the same page, treat missing params as intentional removals
    if (hasNavigated) {
      const merged = new URLSearchParams(searchParams);

      // Get excluded filter keys for the current page
      const excludedKeys = getCurrentModuleFilterExclusions();

      missingKeys.forEach((key) => {
        // Skip restoration if this key is excluded on the current page
        if (excludedKeys.includes(key)) {
          return;
        }

        lastSearchParams.getAll(key).forEach((value) => {
          if (!merged.getAll(key).includes(value)) {
            merged.append(key, value);
          }
        });
      });

      parseParams(merged, parsedKeys, filtersConfig);
      setSearchParams(merged);
    } else {
      parseParams(searchParams, parsedKeys, filtersConfig);
    }
  }, [
    location,
    isLoading,
    isPaymentsList,
    isObsDashboard,
    lastSearchParams,
    searchParams,
    lastPathname,
  ]);

  const setFilterParams = useCallback(
    (filters: Map<string, UniversalFilter>) => {
      setSearchParams((prevParams) => {
        const newParams = new URLSearchParams(prevParams);
        Array.from(parsedKeys).forEach((param) => newParams.delete(param));

        modifyUniversalParams(filters, newParams, ActionEnum.APPEND);
        return newParams;
      });
    },
    [setSearchParams, parsedKeys],
  );

  const clearParams = useCallback(
    (additionalParams: string[] = []) => {
      removeParams([...Array.from(parsedKeys), ...additionalParams]);
    },
    [parsedKeys, removeParams],
  );

  const removeFilterParams = useCallback(
    (filters: Map<string, UniversalFilter>) => {
      setSearchParams((prevParams) => {
        const newParams = new URLSearchParams(prevParams);
        modifyUniversalParams(filters, newParams, ActionEnum.DELETE);
        return newParams;
      });
    },
    [setSearchParams],
  );

  return (
    <UniversalFiltersContext.Provider
      value={{
        lastSearchParams,
        setLastSearchParams,
        setFilterParams,
        clearParams,
        addParams,
        removeFilterParams,
        isLoading,
        universalFilters: filteredUniversalFilters,
        quickFilterMap,
        filtersConfig,
      }}
    >
      {isLoading || !paramsInitialized ? <Loading page /> : children}
    </UniversalFiltersContext.Provider>
  );
};
